/**
 * bmdCortexCore.js — BMD по Pickhardt через кортикальный ободок (для живого runBMD).
 *
 * Метод: на поясничных срезах в задне-центральной зоне находим тело позвонка по
 * КОРТИКАЛЬНОМУ ОБОДКУ (≥250 HU, плотный даже при остеопорозе); заливкой «снаружи»
 * выделяем ЗАМКНУТЫЕ полости, берём САМУЮ БОЛЬШУЮ (это тело позвонка, а не спинномозговой
 * канал/отверстия — защита от ложного занижения по ликвору). Внутри меряем средний HU
 * губчатой кости БЕЗ порога 200. Пороги Pickhardt (нативная фаза): >160 норма,
 * 110–160 погранично, <110 подозрение на остеопороз. Параметры вынесены.
 */
export function bmdCortex(scalarData, dims, opts = {}) {
  const [nx, ny, nz] = dims;
  const sliceSize = nx * ny;
  const CORTEX_HU = opts.cortexHU ?? 250;
  const TRAB_MIN = opts.trabMin ?? 30;         // отсечь ликвор/жидкость (<30), сохранить кость
  const HU_NORMAL = opts.huNormal ?? 160;
  const HU_OSTEOPOROSIS = opts.huOsteoporosis ?? 110;
  const zStart = Math.floor(nz * (opts.zStart ?? 0.55));
  const zEnd = Math.floor(nz * (opts.zEnd ?? 0.85));
  const xStart = Math.floor(nx * (opts.xStart ?? 0.35)), xEnd = Math.floor(nx * (opts.xEnd ?? 0.65));
  const yStart = Math.floor(ny * (opts.yStart ?? 0.50)), yEnd = Math.floor(ny * (opts.yEnd ?? 0.82));
  const roiW = xEnd - xStart, roiH = yEnd - yStart;

  let trabSum = 0, trabCount = 0, sliceHits = 0;
  const perSliceHU = [];
  if (roiW < 3 || roiH < 3) return _undetermined(0, 0);

  for (let z = zStart; z < zEnd; z++) {
    const off = z * sliceSize;
    // 1) кортекс
    const cortex = new Uint8Array(roiW * roiH);
    for (let j = 0; j < roiH; j++) {
      const rowOff = off + (yStart + j) * nx + xStart;
      for (let i = 0; i < roiW; i++) if (scalarData[rowOff + i] >= CORTEX_HU) cortex[j * roiW + i] = 1;
    }
    // 2) заливка снаружи через не-кортекс
    const outside = new Uint8Array(roiW * roiH);
    const stk = [];
    const push = (j, i) => { const k = j * roiW + i; if (!cortex[k] && !outside[k]) { outside[k] = 1; stk.push(k); } };
    for (let i = 0; i < roiW; i++) { push(0, i); push(roiH - 1, i); }
    for (let j = 0; j < roiH; j++) { push(j, 0); push(j, roiW - 1); }
    while (stk.length) { const k = stk.pop(); const j = (k / roiW) | 0, i = k % roiW;
      if (i > 0) push(j, i - 1); if (i < roiW - 1) push(j, i + 1); if (j > 0) push(j - 1, i); if (j < roiH - 1) push(j + 1, i); }
    // 3) interior = не кортекс И не снаружи → замкнутые полости; берём КРУПНЕЙШУЮ
    const interior = new Uint8Array(roiW * roiH);
    for (let k = 0; k < roiW * roiH; k++) if (!cortex[k] && !outside[k]) interior[k] = 1;
    const best = _largestComponent(interior, roiW, roiH);
    if (!best || best.length < 20) continue;
    // 4) средний HU губчатой в крупнейшей полости
    let sSum = 0, sCount = 0;
    for (const k of best) {
      const j = (k / roiW) | 0, i = k % roiW;
      const hu = scalarData[off + (yStart + j) * nx + xStart + i];
      if (hu >= TRAB_MIN && hu < CORTEX_HU) { sSum += hu; sCount++; trabSum += hu; trabCount++; }
    }
    if (sCount >= 20) { sliceHits++; perSliceHU.push(sSum / sCount); }
  }

  if (trabCount < 50 || sliceHits < 2) return _undetermined(sliceHits, trabCount);

  const trabecularHU = trabSum / trabCount;
  let category, catColor;
  if (trabecularHU >= HU_NORMAL) { category = 'Норма'; catColor = '#22c55e'; }
  else if (trabecularHU >= HU_OSTEOPOROSIS) { category = 'Остеопения (погранично)'; catColor = '#eab308'; }
  else { category = 'Подозрение на остеопороз'; catColor = '#ef4444'; }
  return {
    trabecularHU, category, catColor, slices: sliceHits, trabVoxels: trabCount,
    minHU: Math.min(...perSliceHU), maxHU: Math.max(...perSliceHU),
    method: 'Pickhardt HU (позвонок по ободку)',
    note: 'Скрининг по HU губчатой кости, нативная фаза; авто-ROI приблизительный; подтвердить DXA/QCT.',
  };
}

function _undetermined(slices, trabVoxels) {
  return {
    trabecularHU: null, category: 'Позвонки не локализованы', catColor: '#888',
    slices, trabVoxels, method: 'Pickhardt HU (позвонок по ободку)',
    note: 'Нет замкнутого кортикального ободка в зоне (тонкий/разорванный кортекс или нестандартная ориентация). Нужен ручной ROI.',
  };
}

/** Крупнейшая 4-связная компонента маски. Возвращает массив индексов или null. */
function _largestComponent(mask, w, h) {
  const seen = new Uint8Array(w * h);
  let best = null;
  for (let s = 0; s < w * h; s++) {
    if (!mask[s] || seen[s]) continue;
    seen[s] = 1; const q = [s]; let head = 0; const comp = [];
    while (head < q.length) {
      const k = q[head++]; comp.push(k);
      const j = (k / w) | 0, i = k % w;
      const nb = [i > 0 ? k - 1 : -1, i < w - 1 ? k + 1 : -1, j > 0 ? k - w : -1, j < h - 1 ? k + w : -1];
      for (const nk of nb) if (nk >= 0 && mask[nk] && !seen[nk]) { seen[nk] = 1; q.push(nk); }
    }
    if (!best || comp.length > best.length) best = comp;
  }
  return best;
}

export default { bmdCortex };
